/**
 * Chrome for the public technology and university pages: the site's nav
 * (NewNavBar when signed out; the same bar with "Open Cosmos" instead of
 * "Log In / Sign Up" when signed in — NewNavBar has no signed-in variant),
 * the site footer, and the auth / legal modal stack.
 *
 * Unlike /about or /ip-intel-report, a login or sign-up here returns to the
 * same page, so it is fetched again with the new session and shows what that
 * account may see (utils/returnAfterAuth — owner, 21 + 23 Oct 2026):
 *   - an existing account (password / OTP login) reloads this page;
 *   - a NEW account (OTP sign-up here) goes to the app at "/?next=<page>"
 *     first, for onboarding, and comes back here when it is finished or
 *     skipped (App.tsx);
 *   - Google / LinkedIn leave for Clerk: /sso-callback and "/" carry
 *     ?next=<page>, and MainWithClerk / App.tsx bring them back the same way.
 * The page is remembered in sessionStorage when the modal opens (30 minutes,
 * used once) and forgotten when the visitor closes the modal.
 *
 * Pages reach the shell through useTpShell(): openLogin(source) for the
 * locked sections and CTAs, and go(path) for links (client-side for page
 * paths, a full load for the app's own routes).
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NewNavBar, NavItem, NewFooter } from '../NewHome';
import { SubmarkLogo } from '../Icons';
import LoginModal from '../LoginModal';
import SignUpModal from '../SignUpModal';
import { PrivacyPolicyModal } from '../PrivacyPolicyModal';
import { TermsOfServiceModal } from '../TermsOfServiceModal';
import { trackClick } from '../../../utils/analytics';
import { ROUTE_TITLES } from '../../../utils/pageTitle';
import { hasStoredSession } from './api';
import {
  clearReturnAfterAuth, readReturnAfterAuth, ReturnVia, setReturnAfterAuth, updateReturnAfterAuth,
} from '../../../utils/returnAfterAuth';
import { goBackAfterAuth, goToOnboardingThenBack } from '../../../utils/returnAfterAuthNav';
import { TechPageStyles } from './styles';
import { tpClip, tpTrack } from './track';

interface ShellApi {
  /** A verified session (the nav's signed-in variant). */
  signedIn: boolean;
  /** `next`: where to go once signed in (default: reload this page as the new account). */
  /** `heading` / `subheading` retitle the login modal for this prompt. */
  openLogin: (source: string, next?: string, opts?: { heading?: string; subheading?: string }) => void;
  openSignUp: (source: string, next?: string) => void;
  /** Follow a link: plain left clicks on page paths stay in the SPA. */
  go: (path: string, e?: React.MouseEvent) => void;
}

const ShellCtx = createContext<ShellApi>({
  signedIn: false,
  openLogin: () => {},
  openSignUp: () => {},
  go: (path: string) => { window.location.href = path; },
});

export const useTpShell = (): ShellApi => useContext(ShellCtx);

const hardGo = (href: string) => { window.location.href = href; };

/** This page, to come back to after signing in. */
const herePath = (): string => {
  try { return window.location.pathname + window.location.search; } catch { return ''; }
};

/** The app's own routes load fully (their pages record their own pageview
 *  and "/" needs MainWithClerk); only server-resolved page paths navigate
 *  inside the SPA. */
export const isAppRoute = (path: string): boolean => {
  const p = path.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  if (p === '/' || /^https?:/i.test(path)) return true;
  if (ROUTE_TITLES[p] || ROUTE_TITLES[p.toLowerCase()]) return true;
  return /^\/(ip-intel-report|admin|about|comet|rd-symposium|privacy-policy|terms-of-service|sso-callback)(\/|$)/i.test(p);
};

/** True for clicks the browser should handle itself (new tab/window). */
const isModifiedClick = (e?: React.MouseEvent): boolean =>
  !!e && (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey);

interface Props {
  signedIn: boolean;
  /** Called for footer links that leave the site (host only). */
  onOutbound?: (host: string) => void;
  children: React.ReactNode;
}

const NAV_ITEMS = (active: boolean): NavItem[] => [
  { id: 'cosmos', label: 'Cosmos', href: '/', onClick: () => hardGo('/') },
  { id: 'universities', label: 'Universities', href: '/universities', active, onClick: () => hardGo('/universities') },
  { id: 'about_us', label: 'About Us', href: '/about', onClick: () => hardGo('/about') },
];

const IP_INTEL: NavItem = { id: 'ip_intel_report', label: 'IP Intel Report', href: '/ip-intel-report', onClick: () => hardGo('/ip-intel-report') };

/** NewNavBar's markup with the signed-in action ("Open Cosmos"). */
const SignedInNavBar: React.FC<{ items: NavItem[] }> = ({ items }) => {
  const all = items.some(i => i.id === IP_INTEL.id) ? items : [...items, IP_INTEL];
  const open = (where: string) => { trackClick(where); hardGo('/'); };
  return (
    <header className="sticky top-0 z-40 w-full bg-white/90 backdrop-blur-md border-b border-grayscale-200">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20">
        <button
          onClick={() => { trackClick('nav_logo'); hardGo('/'); }}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          aria-label="Go to homepage"
        >
          <SubmarkLogo />
        </button>
        <nav className="hidden md:flex items-center gap-6 lg:gap-8">
          {all.map(item => (
            <a
              key={item.id}
              href={item.href}
              onClick={e => { trackClick(`nav_${item.id}`); if (item.onClick && !isModifiedClick(e)) { e.preventDefault(); item.onClick(); } }}
              className={item.active
                ? 'text-[#725737] font-bold text-sm border-b-2 border-[#886A44] pb-1 -mb-[6px]'
                : 'text-grayscale-1200 hover:text-grayscale-1300 font-medium text-sm transition-colors'}
              {...(item.active ? { 'aria-current': 'page' as const } : {})}
            >{item.label}</a>
          ))}
          <button
            onClick={() => open('nav_open_cosmos')}
            className="px-5 py-2 rounded-full bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm transition-colors shadow-sm"
          >Open Cosmos</button>
        </nav>
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={() => open('nav_open_cosmos_mobile')}
            className="px-3.5 py-1.5 rounded-full bg-primary-600 hover:bg-primary-700 text-white font-medium text-xs sm:text-sm transition-colors shadow-sm whitespace-nowrap"
          >Open Cosmos</button>
        </div>
      </div>
    </header>
  );
};

export const PublicShell: React.FC<Props> = ({ signedIn, onOutbound, children }) => {
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const sourceRef = useRef('nav');
  const nextRef = useRef<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const outboundRef = useRef(onOutbound);
  outboundRef.current = onOutbound;

  const [loginText, setLoginText] = useState<{ heading?: string; subheading?: string }>({});
  /** Signed in (or signed up) through these modals: the closing modal must
   *  not forget the page we are about to go back to. */
  const authDoneRef = useRef(false);
  const loginOpenRef = useRef(false);
  useEffect(() => { loginOpenRef.current = showLogin; }, [showLogin]);

  /** No `next`: come back to this page after auth. A `next` (e.g. "/" for
   *  "Explore … in Cosmos") keeps its own intent, so no return is stored. */
  const rememberReturn = (source: string) => {
    if (nextRef.current) clearReturnAfterAuth();
    else setReturnAfterAuth(herePath(), source);
  };

  const openLogin = useCallback((source: string, next?: string, opts?: { heading?: string; subheading?: string }) => {
    sourceRef.current = source;
    nextRef.current = next && next.startsWith('/') && !next.startsWith('//') ? next : null;
    rememberReturn(source);
    setLoginText(opts || {});
    tpTrack('techpage_login_open', { source });
    setShowSignUp(false);
    setShowLogin(true);
  }, []);

  const openSignUp = useCallback((source: string, next?: string) => {
    sourceRef.current = source;
    if (next !== undefined) nextRef.current = next && next.startsWith('/') && !next.startsWith('//') ? next : null;
    // From the login modal the page is already remembered; otherwise now.
    if (next !== undefined || !readReturnAfterAuth()) rememberReturn(source);
    tpTrack('techpage_signup_open', { source });
    setShowLogin(false);
    setShowSignUp(true);
  }, []);

  const go = useCallback((path: string, e?: React.MouseEvent) => {
    if (isModifiedClick(e)) return;
    e?.preventDefault();
    if (isAppRoute(path)) { hardGo(path); return; }
    navigate(path);
    try { window.scrollTo({ top: 0 }); } catch { /* ignore */ }
  }, [navigate]);

  // Footer links that leave the site (LinkedIn, WhatsApp, …): a
  // page-prefixed outbound event, by delegation — the footer is NewHome's.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      const a = t?.closest('footer a') as HTMLAnchorElement | null;
      if (!a || !root.contains(a)) return;
      const href = a.getAttribute('href') || '';
      if (!/^https?:\/\//i.test(href)) return;
      let host = '';
      try { host = new URL(href).host; } catch { /* ignore */ }
      if (host && host !== window.location.host) outboundRef.current?.(tpClip(host, 80) || '');
    };
    root.addEventListener('click', onClick, true);
    return () => root.removeEventListener('click', onClick, true);
  }, []);

  const api = useMemo<ShellApi>(() => ({ signedIn, openLogin, openSignUp, go }), [signedIn, openLogin, openSignUp, go]);
  const items = NAV_ITEMS(true);

  return (
    <ShellCtx.Provider value={api}>
      <div className="tpg tpg-shell" ref={rootRef}>
        <TechPageStyles />
        {signedIn ? (
          <SignedInNavBar items={items} />
        ) : (
          <NewNavBar
            navItems={items}
            onLoginClick={() => openLogin('nav')}
            onLogoClick={() => hardGo('/')}
          />
        )}

        <main>{children}</main>

        <NewFooter
          onShowAboutUs={() => hardGo('/about')}
          onShowPrivacy={() => setShowPrivacy(true)}
          onShowTerms={() => setShowTerms(true)}
          onShowRdSymposium={() => hardGo('/rd-symposium')}
          onShowUniversities={() => hardGo('/universities')}
          onShowComet={() => hardGo('/comet')}
        />

        {showPrivacy && <PrivacyPolicyModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />}
        {showTerms && <TermsOfServiceModal isOpen={showTerms} onClose={() => setShowTerms(false)} />}
        {showLogin && (
          <LoginModal
            returnAfterAuth
            heading={loginText.heading}
            subheading={loginText.subheading}
            onClose={() => {
              setShowLogin(false);
              if (!authDoneRef.current) clearReturnAfterAuth();
            }}
            // LoginModal has stored the session by the time this fires. Back
            // to this same URL so the page is fetched again as the account.
            onLogin={(_email, method) => {
              authDoneRef.current = true;
              tpTrack('techpage_login_success', { source: sourceRef.current });
              setShowLogin(false);
              if (nextRef.current) { window.location.href = nextRef.current; return; }
              const back = readReturnAfterAuth();
              // The account was made a moment ago in the sign-up modal (which
              // could not sign it in): onboarding first, then back here.
              if (back?.newAccount) { goToOnboardingThenBack(back.path); return; }
              if (back && goBackAfterAuth(back, { newAccount: false, via: (method || 'login') as ReturnVia })) return;
              window.location.reload();
            }}
            onSignUpClick={() => openSignUp(`${sourceRef.current}:login_modal`)}
          />
        )}
        {showSignUp && (
          <SignUpModal
            // This shell navigates itself after a sign-up (below); the
            // modal's own reload would race it and land back here.
            skipReloadOnSuccess
            returnAfterAuth
            onClose={() => {
              setShowSignUp(false);
              // Not when it closes because the login modal took over.
              if (!authDoneRef.current && !loginOpenRef.current) clearReturnAfterAuth();
            }}
            onSignupSuccess={() => {
              tpTrack('techpage_signup_success', { source: sourceRef.current });
              updateReturnAfterAuth({ newAccount: true, via: 'otp_signup' });
              // Signed in straight away (OTP sign-up): a new account, so
              // onboarding in the app first, then back to this page.
              // Otherwise the account exists but needs a login.
              if (hasStoredSession()) {
                authDoneRef.current = true;
                if (nextRef.current) { window.location.href = nextRef.current; return; }
                const back = readReturnAfterAuth();
                if (back) { goToOnboardingThenBack(back.path); return; }
                window.location.reload();
                return;
              }
              setShowSignUp(false);
              setShowLogin(true);
            }}
          />
        )}
      </div>
    </ShellCtx.Provider>
  );
};
